import { ReactNode, useId, useLayoutEffect } from 'react';
import { MarcoDeLaApp, useMarcoDeLaApp, type VarianteDelMarco } from './marco-de-la-app';

interface AppLayoutProps {
  children: ReactNode;
  /*
    Las nueve pantallas de `/plataforma/*` actúan sobre OTRA empresa, no sobre
    "mi empresa": dibujan `BarraPlataforma` en vez del `Sidebar` del portal. El
    resto del layout (navbar, banners, asistente) se queda igual — es solo el
    menú lateral el que cambia según en qué plano estás parado.
  */
  variante?: VarianteDelMarco;
  /*
    F5 (E7, H8): pantalla tipo aplicación, para el editor del Pricer. Sin los 176 px de relleno
    de abajo ni scroll propio del contenido: cada columna de la pantalla scrollea sola. Y el
    contenido NO se remonta cuando cambia la ruta (al guardar una cotización nueva se pasa de
    `/new` a `/:id`, y `?paso=` también): se conservan el foco, el scroll y lo que se esté
    escribiendo. Opcional y aditiva: sin ella el layout es el de siempre, idéntico.
  */
  pantallaCompleta?: boolean;
}

/**
 * El marco de una pantalla de la aplicación: menú lateral, barra de arriba,
 * avisos y botones flotantes.
 *
 * Adentro del router el marco es UNO solo y queda quieto al navegar
 * (`MarcoDeLaApp`, ver ahí el porqué): esto sólo lo pide y pone el contenido.
 * Fuera de él —una prueba que monta la pantalla sola— arma su propio marco.
 */
export function AppLayout({ children, variante = 'portal', pantallaCompleta }: AppLayoutProps) {
  const marco = useMarcoDeLaApp();
  const contenido = (
    <EnElMarco variante={variante} pantallaCompleta={!!pantallaCompleta}>
      {children}
    </EnElMarco>
  );
  if (marco) return contenido;
  return <MarcoDeLaApp propio={{ pantallaCompleta: !!pantallaCompleta }}>{contenido}</MarcoDeLaApp>;
}

function EnElMarco({
  children,
  variante,
  pantallaCompleta,
}: {
  children: ReactNode;
  variante: VarianteDelMarco;
  pantallaCompleta: boolean;
}) {
  const marco = useMarcoDeLaApp();
  const id = useId();
  // Antes de pintar: la pantalla nunca se ve sin su marco.
  useLayoutEffect(() => {
    if (!marco) return;
    marco.pedir(id, { variante, pantallaCompleta });
    return () => marco.soltar(id);
  }, [marco, id, variante, pantallaCompleta]);
  return <main className={pantallaCompleta ? 'h-full' : undefined}>{children}</main>;
}

export default AppLayout;
